import { api } from './client';
import type { User } from '../types/auth';

const API_URL = import.meta.env.VITE_API_URL;

/** Error code returned by POST /api/auth/token when the account hasn't verified its email yet. */
export const ACCOUNT_UNVERIFIED_ERROR = 'ACCOUNT_UNVERIFIED';

export interface TokenData {
  accessToken: string;
  provincesByWorld: Record<string, string>;
}

export interface FetchTokenResult {
  data: TokenData | null;
  /**
   * Backend error CODE (e.g. ACCOUNT_UNVERIFIED_ERROR), not a human-readable message — callers
   * compare it for equality (see AuthContext.login()). This is the one place in this file where
   * `error` is a code: every other function below (loginUser, registerUser, verifyEmailToken,
   * resendVerificationEmail) returns `error` as a human-readable message via the shared `api.post`
   * helper, since fetchAccessToken() is the only caller that needs to branch on the specific
   * failure reason rather than just display it.
   */
  error: string | null;
}

export async function fetchAccessToken(): Promise<FetchTokenResult> {
  try {
    const response = await fetch(`${API_URL}/api/auth/token`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return {
        data: {
          accessToken: data.accessToken,
          provincesByWorld: data.provincesByWorld || {},
        },
        error: null,
      };
    }

    const errorBody = await response.json().catch(() => null);
    return { data: null, error: errorBody?.error ?? null };
  } catch (error) {
    console.error('Failed to fetch access token:', error);
  }
  return { data: null, error: null };
}

interface BackendAccountInfo {
  accountId: string;
  email: string;
  emailVerified: boolean;
  provinces: Array<{
    provinceId: string;
    provinceName: string;
    worldId: string;
    worldName: string;
  }>;
  createdAt: string;
}

export async function fetchUser(token: string): Promise<User | null> {
  try {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data: BackendAccountInfo = await response.json();
      // Transform backend response to frontend User type
      // provincesByWorld and currentWorldId will be set from token response, not from /me
      return {
        accountId: data.accountId,
        email: data.email,
        displayName: data.email.split('@')[0], // Backend doesn't have displayName, derive from email
        provincesByWorld: {}, // Will be overwritten by token response
        currentWorldId: null, // Will be overwritten by token response
      };
    }
  } catch (error) {
    console.error('Failed to fetch user:', error);
  }
  return null;
}

export async function loginUser(
  email: string,
  password: string,
  rememberMe: boolean
): Promise<{ error: string | null }> {
  const { error } = await api.post('/api/auth/login', {
    email,
    password,
    rememberMe,
  });

  return { error };
}

export async function registerUser(
  email: string,
  password: string
): Promise<{ error: string | null }> {
  // Note: displayName is derived from email on frontend, not stored on backend
  const { error } = await api.post('/api/auth/register', {
    email,
    password,
  });

  return { error };
}

export async function logoutUser(): Promise<void> {
  try {
    await fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout error:', error);
  }
}

export async function verifyEmailToken(token: string): Promise<{ error: string | null }> {
  const { error } = await api.post('/api/auth/verify-email', { token });
  return { error };
}

export async function resendVerificationEmail(email: string): Promise<{ error: string | null }> {
  const { error } = await api.post('/api/auth/resend-verification', { email });
  return { error };
}

export async function forgotPassword(email: string): Promise<{ error: string | null }> {
  const { error } = await api.post('/api/auth/forgot-password', { email });
  return { error };
}

export interface ResetPasswordResult {
  /**
   * Backend error CODE (e.g. "INVALID_PASSWORD_RESET_TOKEN"), not a human-readable message —
   * ResetPassword.tsx branches on this to distinguish an invalid/expired token (full-page error
   * state) from a password-validation failure (inline form error, using `errorMessage`). Uses raw
   * `fetch` rather than the shared `api.post` helper for the same reason `fetchAccessToken` does
   * (see its `FetchTokenResult` docstring): `api.post` only surfaces `data.message`, never
   * `data.error`, which is sufficient for every other function in this file since none of them
   * need to branch on the specific failure reason.
   */
  errorCode: string | null;
  errorMessage: string | null;
}

export async function resetPassword(
  token: string,
  newPassword: string,
  confirmNewPassword: string
): Promise<ResetPasswordResult> {
  try {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword, confirmNewPassword }),
    });

    if (response.ok) {
      return { errorCode: null, errorMessage: null };
    }

    const errorBody = await response.json().catch(() => null);
    return {
      errorCode: errorBody?.error ?? null,
      errorMessage: errorBody?.message ?? 'An error occurred',
    };
  } catch (error) {
    console.error('Failed to reset password:', error);
    return { errorCode: null, errorMessage: 'Network error. Please check your connection.' };
  }
}
